import React, { useState } from 'react';
import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ErpAgentActions from '../../actions/erpAgentActions';

import EsmsDialog from '../../components/EsmsDialog';
import ConfirmButton from '../../components/ConfirmButton';
import CancelButton from '../../components/CancelButton';

import classes from './RotateCredentialsModal.module.css';

const ValidationSchema = Yup.object().shape({
    rotate: Yup.string()
        .required()
});

const ErpAgentInformationPanel = ({
    agent,
    authKey,
    clearErrors,
    clearNewAgent,
    isLoading,
    isOpen,
    handleClose,
    newErpAgent,
    rotateErpAgentCredentials,
    serialNumber
}) => {

    const formik = useFormik({
        initialValues: {
            rotate: ''
        },
        validationSchema: ValidationSchema
    });


    const [formIsValid, setFormIsValid] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const [submitted, setSubmitted] = useState(false);


    const handleChange = event => {
        if (event.target.value.toLowerCase() === 'rotate') {
            setFormIsValid(true);
        } else {
            setFormIsValid(false);
        }
        formik.handleChange(event);
    };

    const toggleShowKey = (e) => {
        e.preventDefault();
        setShowKey(!showKey);
    };

    const downloadCredentialsCsv = (e) => {
        e.preventDefault();
        const csv = `serialNumber,authorizationKey\n${serialNumber},${authKey}\n`;
        let hiddenDownload = document.createElement('a');
        hiddenDownload.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
        hiddenDownload.target = '_blank';
        hiddenDownload.download = 'credentials.csv';
        hiddenDownload.click();
    };

    const handleConfirm = async () => {
        let isRotated;
        clearErrors();

        try {
            isRotated = await rotateErpAgentCredentials(agent.id);
            if (isRotated) {
                setSubmitted(true);
            }

        } catch (error) {
            setSubmitted(false);
        }
    };

    const close = () => {
        handleClose();
        setSubmitted(false);
        clearNewAgent();
    };

    return (
        <EsmsDialog
            open={isOpen}
            hideButtons
            onClose={close}
            title={`Rotate Integration Credentials: ${agent.name}`}
            disabled={!formIsValid}
        >
            {
                !submitted &&
                <div className={classes.formColumn}>
                    <div className={classes.labelColumn}>
                        <div className={classes.labelHeader}>
                            <img src="/assets/images/resetIconColor.svg" alt="rotate agent icon" />
                            <span>Rotate Integration Credentials</span>
                        </div>
                        <div className={classes.labelContent}>
                            <p>Rotating Integration Credentials will delete the current Integration Authorization Key and generate a new one. The FSM Integration using current credentials will require the newly generated authorization key to be applied to the FSM Integration software.</p>
                            <p>Are you sure you want to rotate Integration credentials for {agent.name}?</p>
                            <p>
                                To confirm, please type the word <b>ROTATE</b> in the field below.
                            </p>
                        </div>
                    </div>
                    <div className={classes.inputColumn}>
                        <div className={classes.inputs}>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="rotate"
                                    name="rotate"
                                    type="text"
                                    value={formik.values.name}
                                    label="Rotate Confirmation"
                                    onChange={handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                    helperText={Boolean(formik.touched.name) && formik.errors.name}
                                />
                            </FormControl>

                        </div>

                        <div className={classes.buttonContainer}>
                            <CancelButton
                                cancel={handleClose}
                            />
                            <ConfirmButton
                                label="Rotate"
                                type="button"
                                confirm={handleConfirm}
                                disabled={!formIsValid}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                </div>
            }
            {
                submitted &&
                <div className={classes.formColumn}>
                    <div className={classes.labelColumn}>
                        <div className={classes.labelHeader}>
                            <img src="/assets/images/lockFlat.svg" alt="lock icon" />
                            <span>Agent Credentials</span>
                        </div>
                        <div className={classes.labelContent}>
                            <p>
                                These credentials are required by the FSM Integration to authenticate with
                                your BOB Company. Please download a copy of these credentials and store
                                them in a secure location as they are only available for download once.
                            </p>
                            <p>
                                You will have the option to rotate your credentials from the FSM Integration
                                management page at any point.
                            </p>
                        </div>
                    </div>
                    <div className={classes.inputColumn}>
                        <div className={classes.inputs}>
                            <h3 className={classes.downloadHeaderText}>Serial Number</h3>
                            <p>{ serialNumber }</p>
                            <h3 className={classes.downloadHeaderText}>Authorization Key</h3>
                            {!showKey && <a href="/#" onClick={toggleShowKey}>View Key</a>}
                            {showKey &&
                            <>
                                <a href="/#" onClick={toggleShowKey}>Hide Key</a>
                                <p>{ authKey }</p>
                            </>
                            }
                        </div>
                        <div className={classes.buttonContainerRotated}>
                            <ConfirmButton
                                label="Download Integration Credentials"
                                type="button"
                                confirm={downloadCredentialsCsv}
                            />
                        </div>
                    </div>
                </div>
            }
        </EsmsDialog>
    );
};

const mapStateToProps = ({erpAgent, ...state}) => {
    const { newErpAgent } = erpAgent;
    let agentSerial = '';
    let agentPassword = '';

    if (newErpAgent) {
        agentSerial = newErpAgent.agentSerial;
        agentPassword = newErpAgent.agentPassword;
    }

    return {
        companyInfo:  state.companyInfo,
        errors:       state.messages.errors,
        isLoading:    erpAgent.erpAgentLoading,
        serialNumber: agentSerial,
        authKey:      agentPassword,
        newErpAgent
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ErpAgentActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ErpAgentInformationPanel);
